import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { ChevronDown, LogOut, User } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface HeaderProps {
  user: {
    id: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    profilePictureUrl?: string;
  };
  hideNotifications?: boolean;
}

export default function Header({ user, hideNotifications = false }: HeaderProps) {
  const [, setLocation] = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Profile picture should come from /auth/me endpoint now
  const profilePictureUrl = user.profilePictureUrl || null;

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await apiRequest("POST", "/auth/logout");
      // Clear the user data from React Query cache
      queryClient.clear();
      // Force a page reload to clear all state and show landing page
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      // Even if logout fails, clear cache and redirect
      queryClient.clear();
      window.location.href = "/";
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = () => {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleDisplay = () => {
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  return (
    <nav className="bg-white border-b border-servify-light-grey sticky top-0 z-50 font-muli" data-testid="header">
      <div className="max-w-7xl mx-auto" style={{ maxWidth: "88rem" }}>
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center" data-testid="header-logo">
            <img 
              src="/servify_logo.jpeg"   
              alt="Servify Logo" 
              className="h-10 w-auto"
            />
            <h1 className="text-lg font-semibold text-servify-purple leading-none">
              Servify LaunchPad
            </h1>
          </div>
          
          {/* User Menu */}
          <div className="flex items-center space-x-4" data-testid="header-user-menu">
            {!hideNotifications && (
              <NotificationBell 
                userId={user.id} 
                userRole={user.role as 'admin' | 'employee'} 
              />
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center space-x-2 hover:bg-servify-white-grey active:bg-servify-light-grey transition-all duration-200 p-1.5 rounded-lg" 
                  data-testid="button-user-menu"
                >
                  <div className="w-8 h-8 bg-servify-purple rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{getInitials()}</span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-servify-dark-grey" data-testid="text-user-name">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-servify-medium-grey" data-testid="text-user-role">{getRoleDisplay()}</p>
                  </div>
                  <ChevronDown className="h-3 w-3 text-servify-medium-grey transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-48 p-1 mt-1 border border-servify-light-grey shadow-lg rounded-lg bg-white"
                sideOffset={4}
                avoidCollisions={true}
                collisionPadding={8}
              >
                {user.role !== 'candidate' && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => setLocation('/profile')}
                      className="flex items-center space-x-2 px-3 py-2.5 rounded-md hover:bg-servify-white-grey hover:text-servify-purple transition-colors duration-200 cursor-pointer"
                      data-testid="button-profile"
                    >
                      <User className="w-4 h-4 text-servify-purple" />
                      <span className="text-sm font-medium">My Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  disabled={isLoading}
                  className="flex items-center space-x-2 px-3 py-2.5 rounded-md hover:bg-servify-white-grey hover:text-servify-purple transition-colors duration-200 cursor-pointer"
                  data-testid="button-logout"
                >
                  <LogOut className="w-4 h-4 text-servify-purple" />
                  <span className="text-sm font-medium">
                    {isLoading ? "Signing out..." : "Sign out"}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}
